import type { ActivitySource } from "@/shared/api/activity-source";
import { apiGet, type QueryParams } from "./client";
import { accountScopedStorageKey } from "@/shared/storage/account-scope";

const CACHE_PREFIX = "tb_public_web_cache_v1:";
const CACHE_VERSION = 1;
const CACHE_STATUS_EVENT = "tb-public-web-cache-status";

/** Browser-only source receipt. It is never entitlement or server request authority. */
export type ClientSourceProof = Readonly<{ version: 1; capturedAt: number; origin: "network" | "cache" | "fallback" }>;
function withSourceProof<T>(value: T, capturedAt: number, origin: ClientSourceProof["origin"]): T {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  // An enumerable reserved field survives editorial object spreads and Query's
  // structural sharing; the transport's current receipt replaces any wire field.
  return { ...value, __tbSourceProof: { version: 1, capturedAt, origin } } as T;
}

export type PersistentCacheStatus =
  | { phase: "idle" }
  | {
      phase: "fallback";
      path: string;
      capturedAt: number;
      failedAt: number;
      ageMs: number;
      errorCode: string;
    };

export type PersistentCacheHit<T> = {
  value: T;
  capturedAt: number;
  expiresAt: number;
  isFresh: boolean;
};

export type PersistentCacheFallbackContext = {
  capturedAt: number;
  failedAt: number;
  ageMs: number;
};

type CacheEnvelope<T> = {
  version: number;
  capturedAt: number;
  expiresAt: number;
  value: T;
};

let cacheStatus: PersistentCacheStatus = { phase: "idle" };

function getStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function toCacheKey(parts: readonly unknown[]): string {
  return accountScopedStorageKey(
    `${CACHE_PREFIX}${parts
      .map((part) =>
        encodeURIComponent(typeof part === "string" ? part : JSON.stringify(part ?? null)),
      )
      .join("|")}`,
  );
}

function notifyCacheStatusChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(CACHE_STATUS_EVENT));
  } catch {
    /* status is best-effort */
  }
}

function errorCodeFrom(err: unknown, fallbackCode = "request_failed"): string {
  if (err && typeof err === "object" && "code" in err) {
    const code = String((err as { code?: unknown }).code || "").trim();
    if (code) return code;
  }
  return fallbackCode;
}

function publishCacheFallback(
  path: string,
  hit: PersistentCacheHit<unknown>,
  err: unknown,
  failedAt = Date.now(),
) {
  cacheStatus = {
    phase: "fallback",
    path,
    capturedAt: hit.capturedAt,
    failedAt,
    ageMs: Math.max(0, failedAt - hit.capturedAt),
    errorCode: errorCodeFrom(err),
  };
  notifyCacheStatusChanged();
}

function clearCacheFallback(path: string) {
  if (cacheStatus.phase !== "fallback" || cacheStatus.path !== path) return;
  cacheStatus = { phase: "idle" };
  notifyCacheStatusChanged();
}

export function getPersistentCacheStatus(): PersistentCacheStatus {
  return cacheStatus;
}

export function onPersistentCacheStatusChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CACHE_STATUS_EVENT, listener);
  return () => window.removeEventListener(CACHE_STATUS_EVENT, listener);
}

export function readPersistentCache<T>(parts: readonly unknown[]): PersistentCacheHit<T> | null {
  const store = getStorage();
  if (!store) return null;
  const key = toCacheKey(parts);
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope<T>;
    if (parsed.version !== CACHE_VERSION || parsed.value === undefined) return null;
    return {
      value: parsed.value,
      capturedAt: Number(parsed.capturedAt) || 0,
      expiresAt: Number(parsed.expiresAt) || 0,
      isFresh: Number(parsed.expiresAt) > Date.now(),
    };
  } catch {
    return null;
  }
}

export function writePersistentCache<T>(parts: readonly unknown[], value: T, ttlMs: number, capturedAt = Date.now()): void {
  const store = getStorage();
  if (!store) return;
  const envelope: CacheEnvelope<T> = {
    version: CACHE_VERSION,
    capturedAt,
    expiresAt: capturedAt + Math.max(1_000, ttlMs),
    value,
  };
  try {
    store.setItem(toCacheKey(parts), JSON.stringify(envelope));
  } catch {
    /* cache writes are opportunistic */
  }
}

function removePersistentCache(parts: readonly unknown[]): void {
  const store = getStorage();
  if (!store) return;
  try {
    store.removeItem(toCacheKey(parts));
  } catch {
    /* ignore */
  }
}

export function invalidatePersistentCache(parts: readonly unknown[]): void {
  removePersistentCache(parts);
}

export async function cachedApiGet<T>(
  cacheParts: readonly unknown[],
  path: string,
  params?: QueryParams,
  opts: {
    ttlMs: number;
    signal?: AbortSignal;
    activitySource?: ActivitySource;
    fallbackCode?: string;
    fallbackAccept?: (value: T) => boolean;
    fallbackTransform?: (value: T, context: PersistentCacheFallbackContext) => T;
    preferFreshCache?: boolean;
    readFallbackCache?: () => PersistentCacheHit<T> | null;
    responseAccept?: (value: T) => boolean;
    responseCommitGuard?: (value: T) => boolean;
    responseRejectCode?: string;
    uncommittedResponseTransform?: (value: T) => T;
    shouldCache?: (value: T) => boolean;
  } = { ttlMs: 60_000 },
): Promise<T> {
  opts.signal?.throwIfAborted();
  const cacheKey = ["GET", path, params || {}, ...cacheParts];
  const hit = readPersistentCache<T>(cacheKey);
  if (opts.preferFreshCache && hit?.isFresh) return withSourceProof(hit.value, hit.capturedAt, "cache");
  try {
    const value = await apiGet<T>(path, params, {
      fallbackCode: opts.fallbackCode,
      activitySource: opts.activitySource,
      signal: opts.signal,
    });
    opts.signal?.throwIfAborted();
    if (opts.responseAccept && !opts.responseAccept(value)) {
      throw Object.assign(new Error(opts.responseRejectCode || "response_rejected"), {
        code: opts.responseRejectCode || "response_rejected",
      });
    }
    // Some callers cross an explicit freshness boundary while an older HTTP
    // request is still in flight. Let those callers return a safe projection
    // without committing the superseded response to persistent storage.
    if (opts.responseCommitGuard && !opts.responseCommitGuard(value)) {
      return withSourceProof(opts.uncommittedResponseTransform ? opts.uncommittedResponseTransform(value) : value, 0, "fallback");
    }
    const capturedAt = Date.now();
    if (!opts.shouldCache || opts.shouldCache(value)) {
      writePersistentCache(cacheKey, value, opts.ttlMs, capturedAt);
    } else {
      removePersistentCache(cacheKey);
    }
    clearCacheFallback(path);
    return withSourceProof(value, capturedAt, "network");
  } catch (err) {
    opts.signal?.throwIfAborted();
    const acceptsFallback = (
      candidate: PersistentCacheHit<T> | null,
    ): candidate is PersistentCacheHit<T> =>
      Boolean(candidate && (!opts.fallbackAccept || opts.fallbackAccept(candidate.value)));
    let fallbackHit = acceptsFallback(hit) ? hit : null;
    if (!fallbackHit && opts.readFallbackCache) {
      try {
        const secondary = opts.readFallbackCache();
        fallbackHit = acceptsFallback(secondary) ? secondary : null;
      } catch {
        // A migration-only cache read must never hide the original request failure.
      }
    }
    if (fallbackHit) {
      const failedAt = Date.now();
      const context: PersistentCacheFallbackContext = {
        capturedAt: fallbackHit.capturedAt,
        failedAt,
        ageMs: Math.max(0, failedAt - fallbackHit.capturedAt),
      };
      const fallbackValue = opts.fallbackTransform
        ? opts.fallbackTransform(fallbackHit.value, context)
        : fallbackHit.value;
      publishCacheFallback(path, fallbackHit, err, failedAt);
      return withSourceProof(fallbackValue, fallbackHit.capturedAt, "fallback");
    }
    throw err;
  }
}
